/**
 * Landing — public marketing surface ("obsidian glass" design).
 *
 * Always dark, regardless of the OS/app theme: the `dark` class on the page
 * wrapper re-scopes the design tokens for everything inside it, and the
 * `landing-dark` marker lets index.css keep the html chrome (overscroll,
 * scrollbars, native form controls) dark too. The authenticated app keeps
 * its own theme toggle — that's why there is no ThemeToggle in this nav.
 *
 * Sections, top to bottom (each in ./landing/):
 *   1. LandingNav    — sticky glass nav
 *   2. Hero          — two-column copy + animated live-dashboard preview,
 *                      with the provider marquee inside it (above the fold)
 *   3. HowItWorks    — #how, 3 steps (install → import → watch)
 *   4. Standout      — differentiators + one-line install snippet
 *   5. Features      — #features, 6 capability cards
 *   6. Pricing       — #pricing, 3 tiers, Pro highlighted
 *   7. Faq           — #faq, native <details> disclosures
 *   8. CtaBand       — closing call-to-action on a rising glow
 *   9. LandingFooter — hairline footer
 *
 * Copy lives in ./landing/content.ts; scroll reveals in ./landing/Reveal.tsx.
 */

import { LandingNav } from './landing/LandingNav';
import { Hero } from './landing/Hero';
import { HowItWorks } from './landing/HowItWorks';
import { Standout } from './landing/Standout';
import { Features } from './landing/Features';
import { Pricing } from './landing/Pricing';
import { Faq } from './landing/Faq';
import { CtaBand } from './landing/CtaBand';
import { LandingFooter } from './landing/LandingFooter';
import { useSeo } from '@/hooks/useSeo';

export default function Landing() {
  useSeo({
    title: 'aicostr — Real-time AI cost tracking for every LLM provider',
    description:
      'One dashboard for OpenAI, Anthropic, Google, Cohere, Mistral, and self-hosted models — real-time AI cost tracking, budgets, forecasts, and anomaly detection, without a proxy in your request path.',
    canonicalPath: '/',
  });
  return (
    <div className="dark landing-dark min-h-screen bg-background text-foreground">
      <LandingNav />
      <main>
        <Hero />
        <HowItWorks />
        <Standout />
        <Features />
        <Pricing />
        <Faq />
        <CtaBand />
      </main>
      <LandingFooter />
    </div>
  );
}
